/**
 * Configuration Forms Hooks
 *
 * Convenience hooks for accessing configuration store slices.
 * These hooks provide optimized access to specific configuration sections
 * and their update methods, replacing Redux-based form state management.
 *
 * @example
 * ```typescript
 * const { config, updateField } = useMenuConfiguration();
 * updateField({ objectTypeValue: 'Pipe' });
 * console.log(config.objectTypeValue); // 'Pipe'
 * ```
 */

import { useConfigurationStore } from '../../stores/useConfigurationStore';

// ============================================================================
// Menu Configuration Hook
// ============================================================================

/**
 * Hook for Menu page configuration
 * Replaces Redux menuSlice
 */
export const useMenuConfiguration = () => {
  const config = useConfigurationStore((state) => state.menuConfiguration);
  const updateField = useConfigurationStore((state) => state.updateMenuConfiguration);

  return { config, updateField };
};

// ============================================================================
// Connector Binding Configuration Hook
// ============================================================================

/**
 * Hook for Connector Binding page configuration
 * Replaces Redux connectorBindingSlice
 */
export const useConnectorBindingConfiguration = () => {
  const config = useConfigurationStore((state) => state.connectorBindingConfiguration);
  const updateField = useConfigurationStore((state) => state.updateConnectorBindingField);
  const setParameterTarget = useConfigurationStore(
    (state) => state.setConnectorBindingParameterTarget
  );
  const setError = useConfigurationStore((state) => state.setConnectorBindingError);
  const clearError = useConfigurationStore((state) => state.clearConnectorBindingError);

  return { config, updateField, setParameterTarget, setError, clearError };
};

// ============================================================================
// Process Properties Configuration Hook
// ============================================================================

/**
 * Hook for Process Properties page configuration
 * Replaces Redux processPropertiesSlice
 */
export const useProcessPropertiesConfiguration = () => {
  const config = useConfigurationStore((state) => state.processPropertiesConfiguration);
  const updateField = useConfigurationStore((state) => state.updateProcessPropertiesField);
  const setError = useConfigurationStore((state) => state.setProcessPropertiesError);

  return { config, updateField, setError };
};

// ============================================================================
// Bolt Configuration Hook
// ============================================================================

/**
 * Hook for Bolt Connection page configuration
 * Replaces Redux boltConnectionSlice
 */
export const useBoltConfiguration = () => {
  const config = useConfigurationStore((state) => state.boltConfiguration);
  const updateField = useConfigurationStore((state) => state.updateBoltField);
  const setParameterTarget = useConfigurationStore((state) => state.setBoltParameterTarget);
  const setProjectParameterCategories = useConfigurationStore(
    (state) => state.setBoltProjectParameterCategories
  );
  const setError = useConfigurationStore((state) => state.setBoltError);

  return { config, updateField, setParameterTarget, setProjectParameterCategories, setError };
};

// ============================================================================
// Additional Categories Configuration Hook
// ============================================================================

/**
 * Hook for Additional Categories page configuration
 * Replaces Redux additionalCategoriesSlice
 */
export const useAdditionalCategoriesConfiguration = () => {
  const config = useConfigurationStore((state) => state.additionalCategoriesConfiguration);
  const updateField = useConfigurationStore((state) => state.updateAdditionalCategoriesField);
  const setParameterTarget = useConfigurationStore(
    (state) => state.setAdditionalCategoriesParameterTarget
  );
  const setProjectParameterCategories = useConfigurationStore(
    (state) => state.setAdditionalCategoriesProjectParameterCategories
  );
  const setError = useConfigurationStore((state) => state.setAdditionalCategoriesError);

  return { config, updateField, setParameterTarget, setProjectParameterCategories, setError };
};

// ============================================================================
// Mapping Configuration Hook
// ============================================================================

/**
 * Hook for Mapping Configuration page
 * Replaces Redux mappingConfigurationSlice
 */
export const useMappingConfiguration = () => {
  const config = useConfigurationStore((state) => state.mappingConfiguration);
  const selectedObjectType = useConfigurationStore((state) => state.selectedObjectType);
  const setSelectedObjectType = useConfigurationStore((state) => state.setSelectedObjectType);
  const updateAttribute = useConfigurationStore((state) => state.updateMappingAttribute);
  const addAttribute = useConfigurationStore((state) => state.addMappingAttribute);
  const removeAttribute = useConfigurationStore((state) => state.removeMappingAttribute);
  const reorderAttributes = useConfigurationStore((state) => state.reorderMappingAttributes);
  const addObjectType = useConfigurationStore((state) => state.addObjectType);
  const removeObjectType = useConfigurationStore((state) => state.removeObjectType);

  return {
    config,
    selectedObjectType,
    setSelectedObjectType,
    updateAttribute,
    addAttribute,
    removeAttribute,
    reorderAttributes,
    addObjectType,
    removeObjectType,
  };
};

// ============================================================================
// General Configuration Actions Hook
// ============================================================================

/**
 * Hook for general configuration actions (save, reset, etc.)
 */
export const useConfigurationActions = () => {
  const isDirty = useConfigurationStore((state) => state.isDirty);
  const markClean = useConfigurationStore((state) => state.markClean);
  const saveConfiguration = useConfigurationStore((state) => state.saveConfiguration);
  const resetConfiguration = useConfigurationStore((state) => state.resetConfiguration);
  const loadConfiguration = useConfigurationStore((state) => state.loadConfiguration);

  return {
    isDirty,
    markClean,
    saveConfiguration,
    resetConfiguration,
    loadConfiguration,
  };
};
